/**
 * Wraps a single-pass update iterator so each update is observed exactly once, whether the
 * output consumes it or the drain after `commit` does. Ignores `return()` from an output that stops
 * early, so the drain can still observe the remainder.
 */
class ObservedUpdates {
    #source;
    #observe;
    constructor(source, observe) {
        this.#source = source[Symbol.iterator]();
        this.#observe = observe;
    }
    [Symbol.iterator]() {
        return this;
    }
    next() {
        const result = this.#source.next();
        if (result.done !== true) {
            this.#observe(result.value);
        }
        return result;
    }
    return() {
        return { done: true, value: undefined };
    }
    drain() {
        while (this.next().done !== true) {
            // Observed in next().
        }
    }
}
export class WindowedKeySource {
    #keyInput;
    #equals;
    #departures;
    /** Rows that left the requested window and wait for the next structural batch or the timeout. */
    #pendingDepartures = new Set();
    #departureTimer;
    #rows = new Map();
    #rowsByIndex = new Map();
    /** Members the output has not received since `attachOutput` or since leaving the window. */
    #outputPending = new Set();
    /** Rows the open batch displaced; those it does not place again leave with the batch. */
    #displaced = new Set();
    #sinks = new Set();
    #lifetime = new DisposableStack();
    #window = null;
    #inTransaction = false;
    /** Whether sinks have been told `begin` for the open transaction (opened on first change). */
    #sinksOpen = false;
    constructor(options) {
        this.#keyInput = options.keyInput;
        this.#equals = options.equals ?? Object.is;
        this.#departures = options.departures;
        this.#lifetime.defer(() => {
            this.#cancelDepartures();
            this.#sinks.clear();
            this.#rows.clear();
            this.#rowsByIndex.clear();
            this.#displaced.clear();
            this.#outputPending.clear();
        });
    }
    /** The requested window; `null` is unbounded. */
    get window() {
        return this.#window;
    }
    /** Number of member rows, including rows with queued departures. */
    get size() {
        return this.#rows.size;
    }
    /** Number of rows whose departure is queued for the next structural batch or the timeout. */
    get pendingDepartures() {
        return this.#pendingDepartures.size;
    }
    connect(sink) {
        if (this.#lifetime.disposed) {
            throw new Error('WindowedKeySource is disposed');
        }
        if (this.#inTransaction) {
            throw new Error('WindowedKeySource cannot connect during a structural change');
        }
        this.#sinks.add(sink);
        sink.begin();
        for (const row of this.#rows.values()) {
            sink.upsert(row.rowId, row.input);
        }
        sink.end();
        return {
            [Symbol.dispose]: () => {
                this.#sinks.delete(sink);
            },
        };
    }
    apply(batch, commit) {
        this.#transaction(() => {
            for (const rowId of batch.deletedRowIds ?? []) {
                const row = this.#rows.get(rowId);
                if (row !== undefined) {
                    this.#remove(row);
                }
            }
            for (const [rowId, rowIndex] of this.#indexEntries(batch)) {
                const row = this.#rows.get(rowId);
                if (row === undefined) {
                    continue;
                }
                if (!this.#inWindow(rowIndex)) {
                    this.#remove(row);
                }
                else if (row.index !== rowIndex) {
                    this.#place(row, rowIndex);
                }
            }
            const updates = new ObservedUpdates(batch.updates, (update) => this.#observe(update));
            commit?.(updates);
            updates.drain();
            for (const row of this.#displaced) {
                if (row.index === null && this.#rows.get(row.rowId) === row) {
                    this.#remove(row);
                }
            }
            this.#displaced.clear();
            // Departures queued by a window change leave with this batch's arrivals.
            this.#commitDepartures();
        });
    }
    setWindow(window) {
        const departures = this.#departures;
        if (departures === undefined) {
            this.#transaction(() => {
                this.#window = window;
                for (const row of this.#rows.values()) {
                    if (this.#outsideWindow(row)) {
                        this.#remove(row);
                    }
                }
            });
            return;
        }
        if (this.#lifetime.disposed) {
            return;
        }
        if (this.#inTransaction) {
            throw new Error('WindowedKeySource structural changes cannot nest');
        }
        this.#window = window;
        // Recomputed rather than accumulated: rows that return to the window stay members.
        this.#pendingDepartures.clear();
        for (const row of this.#rows.values()) {
            if (this.#outsideWindow(row)) {
                this.#pendingDepartures.add(row);
                // The output drops rows outside its window, so a row that returns before departing
                // must be re-reported when the stream delivers it again.
                this.#outputPending.add(row);
            }
        }
        if (this.#pendingDepartures.size === 0) {
            this.#cancelDepartures();
        }
        else {
            // The first queued change starts the clock; later changes in the same wait reuse it.
            this.#departureTimer ??= departures.schedule(departures.timeoutMs, () => {
                this.#departureTimer = undefined;
                this.#transaction(() => this.#commitDepartures());
            });
        }
    }
    reset(commit) {
        this.#transaction(() => {
            this.#cancelDepartures();
            this.#openSinks();
            for (const sink of this.#sinks) {
                sink.reset();
            }
            this.#rows.clear();
            this.#rowsByIndex.clear();
            this.#outputPending.clear();
            commit?.();
        });
    }
    attachOutput() {
        if (this.#lifetime.disposed) {
            return;
        }
        if (this.#inTransaction) {
            throw new Error('WindowedKeySource structural changes cannot nest');
        }
        for (const row of this.#rows.values()) {
            this.#outputPending.add(row);
        }
    }
    [Symbol.dispose]() {
        this.#lifetime.dispose();
    }
    #transaction(change) {
        if (this.#lifetime.disposed) {
            return;
        }
        if (this.#inTransaction) {
            throw new Error('WindowedKeySource structural changes cannot nest');
        }
        this.#inTransaction = true;
        try {
            change();
        }
        finally {
            this.#inTransaction = false;
            if (this.#sinksOpen) {
                this.#sinksOpen = false;
                for (const sink of this.#sinks) {
                    sink.end();
                }
            }
        }
    }
    /** Removes queued departures that are still outside the window, and stops the timeout. */
    #commitDepartures() {
        for (const row of this.#pendingDepartures) {
            if (this.#rows.get(row.rowId) === row && this.#outsideWindow(row)) {
                this.#remove(row);
            }
        }
        this.#cancelDepartures();
    }
    #cancelDepartures() {
        this.#pendingDepartures.clear();
        this.#departureTimer?.[Symbol.dispose]();
        this.#departureTimer = undefined;
    }
    #outsideWindow(row) {
        return row.index === null || !this.#inWindow(row.index);
    }
    #openSinks() {
        if (!this.#sinksOpen) {
            this.#sinksOpen = true;
            for (const sink of this.#sinks) {
                sink.begin();
            }
        }
    }
    #observe(update) {
        if (!this.#inWindow(update.rowIndex)) {
            return;
        }
        const input = this.#keyInput(update.fields);
        let row = this.#rows.get(update.rowId);
        if (row === undefined) {
            row = { rowId: update.rowId, index: null, input };
            this.#rows.set(row.rowId, row);
        }
        else if (this.#equals(row.input, input)) {
            if (row.index !== update.rowIndex) {
                // A move without a key change leaves membership unchanged (AC-KEYS-004).
                this.#place(row, update.rowIndex);
            }
            if (this.#outputPending.delete(row)) {
                // The new output now holds the row: its values must be written again.
                this.#openSinks();
                for (const sink of this.#sinks) {
                    sink.refresh(row.rowId);
                }
            }
            return;
        }
        // A changed key is reported as an upsert, which writes every value anyway.
        this.#outputPending.delete(row);
        row.input = input;
        if (row.index !== update.rowIndex) {
            this.#place(row, update.rowIndex);
        }
        this.#openSinks();
        for (const sink of this.#sinks) {
            sink.upsert(row.rowId, input);
        }
    }
    #remove(row) {
        if (row.index !== null) {
            this.#rowsByIndex.delete(row.index);
        }
        this.#rows.delete(row.rowId);
        this.#outputPending.delete(row);
        this.#openSinks();
        for (const sink of this.#sinks) {
            sink.remove(row.rowId);
        }
    }
    /** Places `row` at `index`; a different row already there loses its known position. */
    #place(row, index) {
        if (row.index !== null) {
            this.#rowsByIndex.delete(row.index);
        }
        const occupant = this.#rowsByIndex.get(index);
        if (occupant !== undefined) {
            occupant.index = null;
            this.#displaced.add(occupant);
        }
        this.#rowsByIndex.set(index, row);
        row.index = index;
    }
    #inWindow(index) {
        const window = this.#window;
        return window === null || (index >= window.firstRow && index < window.firstRow + window.windowSize);
    }
    #indexEntries(batch) {
        if (batch.rowIdToRowIndex !== undefined) {
            return batch.rowIdToRowIndex.entries();
        }
        return (batch.orderedRowIds ?? []).map((rowId, rowIndex) => [rowId, rowIndex]);
    }
}
//# sourceMappingURL=windowedKeySource.js.map